'use strict';


import {
	Box3, BoxGeometry, DoubleSide,
	ExtrudeGeometry,
	Group,
	Mesh,
	MeshBasicMaterial, Path,
	Shape, Spherical,
	Vector2, Vector3,
} from 'three';

import { Apartment } from './Apartment';
import { EXTRUDE_SETTINGS, FLOOR_HEIGHT, SCHEMATIC_SCALE } from './SchematicBuildingModel';
import { Polygon } from '../../../libs/Polygon';
import { BuildingShape } from './BuildingShape';
import { isPerpendicularLines } from '../../../functions';
import { polyLabel } from '../../../libs/PolyLabel';
import { Facing } from './Facing';
// import Worker from '../Worker/apt.worker';

const MIN_DISTANCE = 15;

export class Floor {
	constructor (floorData,scope) {
		this.floorData = floorData;
		// this.maxSpace = maxSpace;
		this.bboxSpace = scope.maxTotalSpace;
		this.maxSpace = this.floorData.totalSpace;
		this.buildingScope = scope;
		this.shape = scope.schematicScope.shape;
		this.maxApartments = scope.maxApartmentsOnTheFloor;
		this.useExposure = this.floorData.apartments.every(el => el.facing.length);
		
		// this.worker = new Worker();
		// this.worker.addEventListener('message',this.#workerResponse.bind(this));
		this.sizeIn = 0;
		
		switch (this.floorData.type) {
			case 'custom':
			case 'corner':
				this.sizeIn = -this.maxSpace / 14;
				break;
			default:
				this.sizeIn = 0.5;
				break;
		}
		
		if (this.useExposure) {
			this.floorData.apartments.forEach(apt => Facing.parse(apt));
			Facing.sort(this.floorData.apartments);
		}
		
		const fit = this.buildingScope.schematicScope.advancedSettings.fit;
		
		if (fit) {
			this.maxSpace = this.bboxSpace;
			if (this.floorData.apartments.length === this.maxApartments) {
				const diff = (this.maxSpace - this.floorData.totalSpace) / this.floorData.apartments.length;
				this.floorData.apartments.forEach(apt => apt.schematicSize += diff);
			}
		}

		// if (this.floorData.apartments.length === this.maxApartments) {
		// 	const diff = (this.maxSpace - this.floorData.totalSpace) / this.floorData.apartments.length;
		// 	this.floorData.apartments.forEach(apt => apt.schematicSize += diff);
		// }

		this.group = new Group();
		this.group.name = `F_${floorData.floor}`;

		this.polygons = {
			in: null,
			out: null,
			bbox: null
		};

		this.apartments = [];
		
		if (this.maxSpace === 0 || this.floorData.apartments.every(apt => apt.schematicSize === 0)){
			this.floorData.apartments.forEach(apt => {
				const plug = new Mesh(new BoxGeometry(0.01,0.01,0.01));
				plug.name = apt.engineId;
				this.group.add(plug);
			});
			return;
		}
		

		this.createPolygons();
		this.align();
		this.createApartments();
	}
	
	align() {
		const align = this.buildingScope.schematicScope.advancedSettings.align;
		if (align === 'Center')
			return;
		
		const floorSizes = this.getSizes(false);
		const difference = floorSizes.bbox.x / 2 + floorSizes.floor.x / 2;
		
		switch (align) {
			case 'West':
				this.polygons.out.moveToCenter(new Vector2(difference,0));
				this.polygons.in.moveToCenter(new Vector2(difference,0));
				break;
			case 'East':
				this.polygons.out.moveToCenter(new Vector2(-difference,0));
				this.polygons.in.moveToCenter(new Vector2(-difference,0));
				break;
			case 'North':
				this.polygons.out.moveToCenter(new Vector2(0,-difference));
				this.polygons.in.moveToCenter(new Vector2(0,-difference));
				break;
			case 'South':
				this.polygons.out.moveToCenter(new Vector2(0,difference));
				this.polygons.in.moveToCenter(new Vector2(0,difference));
				break;
			case 'NorthWest':
				this.polygons.out.moveToCenter(new Vector2(difference,-difference));
				this.polygons.in.moveToCenter(new Vector2(difference,-difference));
				break;
			case 'NorthEast':
				this.polygons.out.moveToCenter(new Vector2(-difference,-difference));
				this.polygons.in.moveToCenter(new Vector2(-difference,-difference));
				break;
			case 'SouthEast':
				this.polygons.out.moveToCenter(new Vector2(-difference,difference));
				this.polygons.in.moveToCenter(new Vector2(-difference,difference));
				break;
			case 'SouthWest':
				this.polygons.out.moveToCenter(new Vector2(difference,difference));
				this.polygons.in.moveToCenter(new Vector2(difference,difference));
				break;
		}
		
	}
	
	getBoxes(useScale) {
		const floorPoints = this.polygons.out.getPoints();
		const maxPoints = this.polygons.bbox.getPoints();
		let floorPointsV3;
		let maxPointsV3;
		if (useScale) {
			floorPointsV3 = floorPoints.map(vec2 => new Vector3(vec2.x,0,vec2.y).multiplyScalar(SCHEMATIC_SCALE));
			maxPointsV3 = maxPoints.map(vec2 => new Vector3(vec2.x,0,vec2.y).multiplyScalar(SCHEMATIC_SCALE));
		} else {
			floorPointsV3 = floorPoints.map(vec2 => new Vector3(vec2.x,0,vec2.y));
			maxPointsV3 = maxPoints.map(vec2 => new Vector3(vec2.x,0,vec2.y));
		}
		const maxBox = new Box3();
		const floorBox = new Box3();
		maxBox.setFromPoints(maxPointsV3);
		floorBox.setFromPoints(floorPointsV3);
		
		return {
			floor: floorBox,
			bbox: maxBox
		}
	}
	
	getSizes(useScale) {
		const {floor,bbox} = this.getBoxes(useScale);
		const sizeFloor = new Vector3();
		const sizeBbox = new Vector3();
		floor.getSize(sizeFloor);
		bbox.getSize(sizeBbox);
		
		return {
			floor: sizeFloor,
			bbox: sizeBbox
		}
	}
	
	// #workerResponse(e) {
	// 	const { result } = e.data;
	//
	// 	result.forEach(data => {
	// 		const points = data.shape.map(ar => new Vector2().fromArray(ar));
	// 		const aptScope = new Apartment(points,data.apt);
	// 		this.group.add(aptScope.createMesh());
	// 		this.apartments.push(aptScope);
	// 	});
	//
	// 	this.worker.terminate();
	// 	this.worker = null;
	// }

	createApartments() {
		const outPoly = this.polygons.out;
		const inPoly = this.polygons.in;
		
		// this.worker.postMessage({
		// 	outPolyPoints: outPoly.getPoints().map(vec => vec.toArray()),
		// 	inPolyPoints: inPoly.getPoints().map(vec => vec.toArray()),
		// 	type: this.floorData.type,
		// 	apartments: this.floorData.apartments,
		// 	maxSpace: this.maxSpace
		// });

		function findApartmentPlace (apt,checkpoint,checkIn = true,checkOut = true,minDistance = MIN_DISTANCE) {
			let checkpointDistance = outPoly.getFullDistance(checkpoint);
			const data = {
				out1: null,
				out2: null,
				in1: null,
				in2: null,
				curve1: false,
				curve2: false,
				offset: 0,
				cp: new Vector2().copy(checkpoint),
				apt: apt
			};

			function getPoints() {
				data.out1 = outPoly.getPointFromDistance(checkpointDistance - apt.schematicSize / 2);
				data.out2 = outPoly.getPointFromDistance(checkpointDistance + apt.schematicSize / 2);
				data.in1 = inPoly.intersects(data.out1);
				data.in2 = inPoly.intersects(data.out2);
			}

			getPoints();

			if (checkIn) {
				if (data.in1.equals(data.in2) || data.in1.distanceTo(data.in2) < minDistance / 2) {
					data.offset += minDistance;
				}
			}

			if (checkOut) {
				if (outPoly.getDistance(data.out2) < minDistance || outPoly.getDistance(data.out1) < minDistance) {
					data.offset += minDistance;
				}
			}

			const curve1 = outPoly.findCurveFromVec(data.out1);
			const curve2 = outPoly.findCurveFromVec(data.out2);

			data.curve1 = isPerpendicularLines(curve1.v1,curve1.v2,data.out1,data.in1);
			data.curve2 = isPerpendicularLines(curve2.v1,curve2.v2,data.out2,data.in2);


			return data;
		}

		function getApartmentsData (apartments,checkpoint,checkIn,checkOut,minDistance,direction = 'clockwise') {
			const apartmentsData = [];
			let offset = 0;

			for (let i = 0; i < apartments.length; i++) {
				let j = (i + 1) % apartments.length;
				const current = apartments[i];
				const next = apartments[j];
				const data = findApartmentPlace(current,checkpoint,checkIn,checkOut,minDistance);
				let d = null;
				
				if (direction === 'clockwise') {
					d = outPoly.getFullDistance(data.out2) + next.schematicSize / 2;
				} else {
					d = outPoly.getFullDistance(data.out1) - next.schematicSize / 2;
				}
				checkpoint.copy(outPoly.getPointFromDistance(d));
				apartmentsData.push(data);
				offset += data.offset;
			}

			return {
				data: apartmentsData,
				offset
			};
		}

		let calculatedPoints = [];
		let globalOffset = 0;
		let count = 0;
		const apartments = this.floorData.apartments;
		const USE_EXPOSURE = this.useExposure;
		const stepCount = USE_EXPOSURE ?  1 / this.maxSpace : 1 / this.maxSpace * 10;
		const TYPE = this.floorData.type;
		const OFFSET = this.buildingScope.schematicScope.advancedSettings.offset;
		
		
		if (apartments.length === 1 && apartments[0].schematicSize === this.maxSpace) {
			const outPoints = outPoly.getPoints();
			const inPoints = inPoly.getPoints();
			
			const aptScope = new Apartment(outPoints,apartments[0]);
			aptScope.outPath = new Path(outPoints);
			aptScope.holes = [new Path().setFromPoints(inPoints)];
			this.group.add(aptScope.createMesh());
			this.apartments.push(aptScope);
			this.createFloorPlug();
			return;
		}

		function fitApartments (checkIn,checkOut,minDistance) {
			globalOffset = 0;
			count = 0;
			const calculated = [];
			let start = 0;
			
			if (USE_EXPOSURE) {
				start = Facing.startCheckpointT(apartments[0]);
				if (OFFSET > 0) {
					start += OFFSET;
					if (start >= 1) {
						start = Math.abs(Math.trunc(start) - start);
					}
				}
				const next = apartments[1];
				let direction = 'clockwise';
				
				if (next) {
					const nextT = Facing.startCheckpointT(next);
					
					if (Math.abs(start - nextT) > 0.5) {
						direction = 'counterclockwise';
					}
				}
				
				if (OFFSET > 0) {
					const checkpoint = outPoly.getPoint(start);
					const {data,offset} = getApartmentsData(apartments,checkpoint,checkIn,checkOut,minDistance,direction);
					calculatedPoints = data;
					return data;
				}
				
				for (let i = start; i < 1; i += stepCount) {
					const checkpoint = outPoly.getPoint(i);
					const {data,offset} = getApartmentsData(apartments,checkpoint,checkIn,checkOut,minDistance,direction);
					const curves = data.reduce((acc,v) => {
						if (!v.curve1) {
							acc++;
						}
						if (!v.curve2) {
							acc++;
						}
						return acc;
					},0);
					
					if ((offset === 0) || TYPE === 'circle') {
						calculatedPoints = data;
						break;
					} else {
						calculated.push({
							offset,
							curves,
							distance: minDistance,
							data
						});
					}
				}
				
			} else {
				if (OFFSET > 0) {
					start += OFFSET;
					if (start >= 1) {
						start = Math.abs(Math.trunc(start) - start);
					}
					const checkpoint = outPoly.getPoint(start);
					const {data,offset} = getApartmentsData(apartments,checkpoint,checkIn,checkOut,minDistance);
					calculatedPoints = data;
					return data;
				}
				
				for (let i = start; i < 1; i += stepCount) {
					const checkpoint = outPoly.getPoint(i);
					const {data,offset} = getApartmentsData(apartments,checkpoint,checkIn,checkOut,minDistance);
					const curves = data.reduce((acc,v) => {
						if (!v.curve1) {
							acc++;
						}
						if (!v.curve2) {
							acc++;
						}
						return acc;
					},0);
					
					if ((offset === 0 && curves === 0) || TYPE === 'circle') {
						calculatedPoints = data;
						break;
					} else {
						calculated.push({
							offset,
							curves,
							distance: minDistance,
							data
						});
					}
				}
			}

			return calculated;
		}

		if (this.floorData.type !== 'circle') {
			let distance = MIN_DISTANCE;
			const calculated = [];
			
			function findOptimalResult (offset = 0) {
				const minElements = calculated.filter(el => el.offset === offset);
				if (minElements.length) {
					minElements.sort((a,b) => a.curves - b.curves && a.distance > b.distance);
					calculatedPoints = minElements[0].data;
				}
			}

			while (distance !== 0) {
				if (calculatedPoints.length) {
					break;
				}
				const data = fitApartments(true,true,distance);
				if (data) {
					calculated.push(...data);
					if (!calculatedPoints.length) {
						findOptimalResult();
					}
				}
				distance--;
			}
			
			if (!calculatedPoints.length) {
				const minOffset = Math.min(...calculated.map(el => el.offset));
				findOptimalResult(minOffset);
			}

		} else {
			fitApartments(false,false,0);
		}
		
		for (let i = 0; i < calculatedPoints.length; i++) {
			const data = calculatedPoints[i];
			let path2;
			let path3;
			
			const path1 = outPoly.getPointsFromRange(data.out1,data.out2,data.cp);
			if (this.floorData.type === 'circle') {
				path2 = [data.out2,data.in2];
				path3 = [data.in1,data.out1];
			} else {
				path2 = Polygon.findPath(data.out2,data.in2,outPoly,inPoly,outPoly);
				path3 = Polygon.findPath(data.in1,data.out1,inPoly,outPoly,outPoly);
			}
			
			const intersectedPoints = inPoly.getPointsFromRange(data.in2,data.in1,data.cp);
			
			let aptPoints = [];
			aptPoints.push(...path1);
			aptPoints.push(...path2);
			aptPoints.push(...intersectedPoints);
			aptPoints.push(...path3);
			
			// const checkPoly = new Polygon(aptPoints);
			// if (Polygon.isCrossLines(checkPoly)) {
			// 	switch (this.floorData.type) {
			// 		case 'custom':
			// 		case 'corner':
			// 			this.sizeIn += MIN_DISTANCE;
			// 			break;
			// 		default:
			// 			this.sizeIn += 0.1;
			// 			break;
			//
			// 	}
			//
			// 	this.apartments = [];
			// 	while (this.group.children.length) {
			// 		this.group.children[0].removeFromParent();
			// 	}
			// 	this.createPolygons();
			// 	this.createApartments();
			// 	return;
			// }
			
			const aptScope = new Apartment(aptPoints,data.apt);
			aptScope.outPath = new Path(path1);
			this.group.add(aptScope.createMesh());
			this.apartments.push(aptScope);
		}
		
		this.createFloorPlug();
	}

	createPolygons() {
		let points = [];

		switch (this.floorData.type) {
			case 'square':
				points = BuildingShape.square();
				this.polygons.bbox = new Polygon(points);
				this.polygons.bbox.moveToCenter(new Vector2());
				this.polygons.bbox.setLength(this.bboxSpace);
				
				this.polygons.out = new Polygon(points);
				this.polygons.out.moveToCenter(new Vector2());
				this.polygons.out.setLength(this.maxSpace);
				
				this.polygons.in = new Polygon(points);
				this.polygons.in.moveToCenter(new Vector2());
				this.polygons.in.setLength(this.maxSpace);
				this.polygons.in.multiplyScalar(this.sizeIn);
			break;
			case 'rectangle':
				points = BuildingShape.rectangle();
				this.polygons.bbox = new Polygon(points);
				this.polygons.bbox.moveToCenter(new Vector2());
				this.polygons.bbox.setLength(this.bboxSpace);
				
				this.polygons.out = new Polygon(points);
				this.polygons.out.moveToCenter(new Vector2());
				this.polygons.out.setLength(this.maxSpace);
				
				this.polygons.in = new Polygon(points);
				this.polygons.in.moveToCenter(new Vector2());
				this.polygons.in.setLength(this.maxSpace);
				this.polygons.in.multiplyScalar(this.sizeIn);
				break;
			case 'circle':
				points = BuildingShape.circle();
				this.polygons.bbox = new Polygon(points);
				this.polygons.bbox.setLength(this.bboxSpace);
				
				this.polygons.out = new Polygon(points);
				this.polygons.out.setLength(this.maxSpace);
				
				this.polygons.in = new Polygon(points);
				this.polygons.in.setLength(this.maxSpace);
				this.polygons.in.multiplyScalar(this.sizeIn);
				break;
			case 'corner':
				points = BuildingShape.corner();
				this.polygons.bbox = new Polygon(points);
				this.polygons.bbox.moveToCenter(new Vector2());
				this.polygons.bbox.setLength(this.bboxSpace);
				
				this.polygons.out = new Polygon(points);
				this.polygons.out.moveToCenter(new Vector2());
				this.polygons.out.setLength(this.maxSpace);
				
				this.polygons.in = new Polygon(points);
				this.polygons.in.moveToCenter(new Vector2());
				this.polygons.in.setLength(this.maxSpace);
				this.polygons.in.offset(this.sizeIn);
				
				break;
			case 'custom':
				this.polygons.bbox = new Polygon(this.shape);
				this.polygons.bbox.moveToCenter(new Vector2());
				this.polygons.bbox.setLength(this.bboxSpace);
				
				this.polygons.out = new Polygon(this.shape);
				this.polygons.out.moveToCenter(new Vector2());
				this.polygons.out.setLength(this.maxSpace);

				this.polygons.in = new Polygon(this.shape);
				this.polygons.in.moveToCenter(new Vector2());
				this.polygons.in.setLength(this.maxSpace);
				this.sizeIn = this.polygons.in.offset(this.sizeIn,0,true);
				break;
				default:
					this.type = 'square';
					points = BuildingShape.square();
					this.polygons.bbox = new Polygon(points);
					this.polygons.bbox.moveToCenter(new Vector2());
					this.polygons.bbox.setLength(this.bboxSpace);
					
					this.polygons.out = new Polygon(points);
					this.polygons.out.moveToCenter(new Vector2());
					this.polygons.out.setLength(this.maxSpace);
					
					this.polygons.in = new Polygon(points);
					this.polygons.in.moveToCenter(new Vector2());
					this.polygons.in.setLength(this.maxSpace);
					this.polygons.in.multiplyScalar(this.sizeIn);
					break;
		}
	}
	
	createFloorPlug() {
		const extrude = JSON.parse(JSON.stringify(EXTRUDE_SETTINGS));
		extrude.depth = 0.1;
		const points = this.polygons.in.getPoints();
		
		const polyArray = points.map(vec => vec.toArray());
		
		const label = polyLabel([polyArray]);
		const centroid = new Vector3(label[0],label[1],0);
		centroid.multiplyScalar(SCHEMATIC_SCALE);
		centroid.applyAxisAngle(new Vector3(1,0.0),-Math.PI / 2);
		centroid.y = 0;
		
		const shape = new Shape(points);
		
		const geometry = new ExtrudeGeometry(shape,extrude);
		geometry.rotateX(-Math.PI / 2);
		geometry.scale(SCHEMATIC_SCALE,1,SCHEMATIC_SCALE);
		const center = new Vector3();
		geometry.computeBoundingBox();
		geometry.boundingBox.getCenter(center);
		center.y = 0;
		const geoPosition = new Vector3().subVectors(center,centroid);
		
		geometry.center();
		geometry.translate(geoPosition.x,0,geoPosition.z);
		centroid.y = FLOOR_HEIGHT - 0.2;
		
		const mesh = new Mesh(geometry,new MeshBasicMaterial());
		mesh.name = `zero_${this.floorData.floor}`;
		mesh.position.copy(centroid);
		this.group.add(mesh);
	}

}



